import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { InterventionActivitiesMissing, InterventionActivityModel, InterventionLadderStepModel } from '../../../api-service/clients';
import { CircularProgressCentered } from '../../../components/circular-progress-centered';
import { formatCpr, formatNumber, formatPeriod } from '../../../utilities';
import { offerAndActivityAreaPathName, viewActivityPathName } from '../../offer-and-activity/offer-and-activity-area-pathnames';

export interface InterventionActivitiesDialogProps {
  step: InterventionLadderStepModel;
  missing: InterventionActivitiesMissing;
  periodLabel: string;
  activities: InterventionActivityModel[] | undefined;
  activitiesOutsideDataBoundary: number;
  hasFailed: boolean;
  onClose: () => void;
}

export function formatActivitiesOutsideDataBoundary(count: number) {
  return count === 1
    ? '1 aktivitet vises ikke, da borgeren er uden for din dataafgrænsning.'
    : `${formatNumber(count)} aktiviteter vises ikke, da borgerne er uden for din dataafgrænsning.`;
}

export function InterventionActivitiesDialog({ step, missing, periodLabel, activities, activitiesOutsideDataBoundary, hasFailed, onClose }: InterventionActivitiesDialogProps) {
  const hasActivitiesOutsideDataBoundary = !hasFailed && activities !== undefined && activitiesOutsideDataBoundary > 0;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {missing === InterventionActivitiesMissing.Price ? 'Aktiviteter uden pris' : 'Aktiviteter uden kendt leverandør'}
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          Trin {step.step} · {step.title} · {periodLabel.toLowerCase()}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <InterventionActivitiesTable activities={activities} hasFailed={hasFailed} hasActivitiesOutsideDataBoundary={hasActivitiesOutsideDataBoundary} />
        {hasActivitiesOutsideDataBoundary && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1.5 }}>{formatActivitiesOutsideDataBoundary(activitiesOutsideDataBoundary)}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Luk</Button>
      </DialogActions>
    </Dialog>
  );
}

interface InterventionActivitiesTableProps {
  activities: InterventionActivityModel[] | undefined;
  hasFailed: boolean;
  hasActivitiesOutsideDataBoundary: boolean;
}

function InterventionActivitiesTable({ activities, hasFailed, hasActivitiesOutsideDataBoundary }: InterventionActivitiesTableProps) {
  if (hasFailed) {
    return <Typography>Aktiviteterne kunne ikke hentes.</Typography>;
  }
  if (activities === undefined) {
    return <CircularProgressCentered />;
  }
  if (activities.length === 0) {
    return hasActivitiesOutsideDataBoundary ? null : <Typography>Der er ingen aktiviteter.</Typography>;
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Borger</TableCell>
          <TableCell>CPR-nummer</TableCell>
          <TableCell>Aktivitet</TableCell>
          <TableCell>Periode</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {activities.map(activity => (
          <TableRow key={activity.activityId}>
            <TableCell>{activity.citizenName ?? '–'}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCpr(activity.cpr, '–')}</TableCell>
            <TableCell>
              <Link href={`/${offerAndActivityAreaPathName}/${viewActivityPathName}/${activity.activityId}`} target="_blank" rel="noopener">
                {activity.title || 'Aktivitet uden titel'}
              </Link>
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatPeriod(activity.startDate, activity.endDate)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface ShowActivitiesLinkProps {
  text: string;
  onShow: () => void;
}

// The link sits inside a selectable box, so its click must not also select the box.
export function ShowActivitiesLink({ text, onShow }: ShowActivitiesLinkProps) {
  return (
    <Link
      component="button"
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
        onShow();
      }}
      sx={{ display: 'block', fontSize: '0.6875rem', mt: 0.25, color: 'text.secondary', textAlign: 'left', textDecorationColor: 'currentColor' }}
    >
      {text}
    </Link>
  );
}
